import React, { useState } from "react";
import {
  LayoutDashboard, FileText, BadgeCheck, Activity,
  Home, Camera, ListChecks, CalendarClock,
  Wrench, Bell, ChevronRight, Download,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Breadcrumbs from "../components/Breadcrumbs";
import { Toaster } from "../components/ui/toaster";
import { ValuationProvider, useAppModals } from "../components/ValuationDialog";
import Seo from "../components/Seo";
import { MYNEWSPACE } from "../mock";

const ICONS = {
  LayoutDashboard, FileText, BadgeCheck, Activity,
  Home, Camera, ListChecks, CalendarClock,
};

const CRUMBS = [{ label: "Home", to: "/" }, { label: "MyNewSpace" }];

function PhoneMockup({ phone }) {
  return (
    <div className="relative mx-auto w-[290px]" data-testid="mynewspace-phone">
      <div className="rounded-[2.75rem] border-[11px] border-[#16323f] bg-[#16323f] shadow-2xl">
        <div key={phone.greeting} className="relative h-[580px] overflow-hidden rounded-[2rem] bg-[#f4f1ea] animate-in fade-in duration-500">
          <div className="absolute left-1/2 top-0 z-10 h-6 w-32 -translate-x-1/2 rounded-b-2xl bg-[#16323f]" />
          <div className="flex h-full flex-col px-4 pb-4 pt-9">
            {/* App bar */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-[#c9a25e]">MyNewSpace</p>
                <p className="text-[15px] font-semibold text-[#16323f]">{phone.greeting}</p>
              </div>
              <div className="grid h-9 w-9 place-items-center rounded-full bg-[#16323f] text-xs font-bold text-white">NS</div>
            </div>

            {/* Stat cards */}
            <div className="mt-4 grid grid-cols-2 gap-3">
              {phone.statCards.map((s) => (
                <div key={s.label} className="rounded-2xl bg-white p-3 shadow-sm">
                  <p className="text-[10px] font-medium uppercase tracking-wide text-[#9a9088]">{s.label}</p>
                  <p className="mt-1 font-display text-2xl font-600 text-[#16323f]">{s.value}</p>
                  <p className="text-[11px] text-[#7a716a]">{s.sub}</p>
                </div>
              ))}
            </div>

            {/* Repair row */}
            <div className="mt-3 rounded-2xl bg-white p-3 shadow-sm">
              <div className="flex items-center gap-2.5">
                <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-[#16323f]/5 text-[#16323f]">
                  <Wrench size={15} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[13px] font-semibold text-[#16323f]">{phone.repair.title}</p>
                  <p className="truncate text-[11px] text-[#7a716a]">{phone.repair.meta}</p>
                </div>
                <span className={`shrink-0 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide ${
                  phone.repair.tone === "green" ? "bg-[#e6f4ea] text-[#1f7a44]" : "bg-[#fbf0dc] text-[#a9761b]"
                }`}>
                  {phone.repair.status}
                </span>
              </div>
            </div>

            {/* Notification row */}
            <div className="mt-3 flex items-center gap-2.5 rounded-2xl bg-white p-3 shadow-sm">
              <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-[#d6336c]/10 text-[#d6336c]">
                <Bell size={15} />
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-[13px] font-semibold text-[#16323f]">{phone.note.title}</p>
                <p className="truncate text-[11px] text-[#7a716a]">{phone.note.desc}</p>
              </div>
              <Download size={15} className="shrink-0 text-[#9a9088]" />
            </div>

            <div className="flex-1" />

            {/* Action button */}
            <button className="mt-3 w-full rounded-full bg-[#d6336c] py-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-white">
              {phone.action}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Content() {
  const { openEnquiry } = useAppModals();
  const [mode, setMode] = useState("landlords");
  const data = MYNEWSPACE[mode];

  return (
    <div className="App">
      <Seo
        title="MyNewSpace | Your Property App for Landlords & Tenants | New Space"
        description="MyNewSpace puts your whole tenancy in your pocket — real-time rent status, accountant-ready statements, one-tap repair approvals and live job tracking for landlords and tenants."
        path="/mynewspace"
      />
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden bg-[#16323f] pt-32 pb-14 lg:pt-40">
        <div className="mx-auto max-w-[1000px] px-5 text-center">
          <div className="mb-6 flex justify-center">
            <Breadcrumbs variant="light" items={CRUMBS} />
          </div>
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#dfa63c]">Your Property App</p>
          <h1 className="mt-4 font-display text-4xl font-700 uppercase leading-tight tracking-wide text-white lg:text-6xl">
            MyNewSpace
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-[15px] leading-relaxed text-white/85">
            Everything about your property, in your pocket. Real-time updates, statements and repairs — no phone calls,
            no waiting for office hours.
          </p>

          {/* Toggle */}
          <div className="mt-8 inline-flex rounded-full bg-white/10 p-1 backdrop-blur" data-testid="mynewspace-toggle">
            {[
              { id: "landlords", label: "For Landlords" },
              { id: "tenants", label: "For Tenants" },
            ].map((opt) => (
              <button
                key={opt.id}
                onClick={() => setMode(opt.id)}
                data-testid={`mynewspace-toggle-${opt.id}`}
                className={`rounded-full px-6 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] transition-colors ${
                  mode === opt.id ? "bg-white text-[#16323f]" : "text-white/75 hover:text-white"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Features + phone */}
      <section className="bg-[#faf7f0] py-16 lg:py-24">
        <div className="mx-auto grid max-w-[1150px] items-center gap-14 px-5 lg:grid-cols-2 lg:gap-16">
          {/* Features */}
          <div key={mode} className="space-y-7 animate-in fade-in slide-in-from-left-4 duration-500" data-testid="mynewspace-features">
            {data.features.map((f) => {
              const Icon = ICONS[f.icon];
              return (
                <div key={f.title} className="flex gap-4">
                  <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-[#16323f] text-[#dfa63c]">
                    <Icon size={22} strokeWidth={1.8} />
                  </span>
                  <div>
                    <h3 className="font-display text-xl font-600 text-[#16323f]">{f.title}</h3>
                    <p className="mt-1.5 text-[15px] leading-relaxed text-[#5b5450]">{f.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Phone */}
          <div className="order-first lg:order-last">
            <PhoneMockup phone={data.phone} />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#16323f] py-16">
        <div className="mx-auto flex max-w-[900px] flex-col items-center px-5 text-center">
          <h2 className="font-display text-3xl font-600 uppercase tracking-wide text-white lg:text-4xl">
            Ready when you are
          </h2>
          <p className="mt-3 max-w-xl text-white/75">
            MyNewSpace comes as standard with every managed tenancy. Ask us how to get set up.
          </p>
          <button
            onClick={() => openEnquiry("MyNewSpace App")}
            data-testid="mynewspace-cta"
            className="group mt-8 flex items-center gap-2 rounded-full bg-[#d6336c] px-9 py-3.5 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:bg-[#bb2c5d]"
          >
            Ask Us About MyNewSpace
            <ChevronRight size={16} className="transition-transform group-hover:translate-x-1" />
          </button>
          <p className="mt-4 text-xs uppercase tracking-[0.2em] text-white/55">Available on iOS &amp; Android</p>
        </div>
      </section>

      <Footer />
      <Toaster />
    </div>
  );
}

export default function MyNewSpace() {
  return (
    <ValuationProvider>
      <Content />
    </ValuationProvider>
  );
}
